import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosInstance } from "axios";
import axiosInstance from "../../axiosInstance/axiosInstance";
import portalAxios from "../../portal/portalAxios";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { MaterialResponse } from "../../materials/contracts/MaterialResponse";
import { MaterialResponseMapper } from "../../materials/mappers/MaterialResponseMapper";
import { Material } from "../../materials/models/Material";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { AttachmentDownloadUrlResponse } from "../contracts/AttachmentDownloadUrlResponse";
import { AttachmentResponse } from "../contracts/AttachmentResponse";
import { AttachmentUploadResponse } from "../contracts/AttachmentUploadResponse";
import { CreateAttachmentRequest } from "../contracts/CreateAttachmentRequest";
import { toMessageAttachment } from "../mappers/AttachmentResponseMapper";
import { MessageAttachment } from "../models/MessageAttachment";
import { MessagesChannel } from "../models/MessagesChannel";

/** An attachment error; `resetsOn` is set when the month's video space is used (`code` "videoQuota"). */
export interface AttachmentError extends ApiErrorResponse {
  resetsOn?: string;
}

const route = (channel: MessagesChannel): { http: AxiosInstance; base: string } =>
  channel.kind === "staff"
    ? { http: axiosInstance, base: "/messages" }
    : { http: portalAxios, base: `/portal/members/${channel.memberId}/messages` };

const toAttachmentError = (error: unknown, fallback: string): AttachmentError => {
  const apiError = toApiError(error, fallback);
  const data = axios.isAxiosError(error) ? error.response?.data : undefined;
  const resetsOn = typeof data?.resetsOn === "string" ? data.resetsOn : undefined;
  return { ...apiError, resetsOn };
};

interface ThreadArgs {
  channel: MessagesChannel;
  threadId: string;
}

/** Begins an upload: the attachment id and the presigned URL its bytes go to. */
export const beginAttachment = createAsyncThunk<
  AttachmentUploadResponse,
  ThreadArgs & { request: CreateAttachmentRequest },
  { rejectValue: AttachmentError }
>("messages/beginAttachment", async ({ channel, threadId, request }, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const response = await http.post<AttachmentUploadResponse>(`${base}/threads/${threadId}/attachments`, request);
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(toAttachmentError(error, "Failed to start the upload"));
  }
});

/** Completes an upload once its bytes are in the bucket. */
export const completeAttachment = createAsyncThunk<
  MessageAttachment,
  ThreadArgs & { attachmentId: string },
  { rejectValue: AttachmentError }
>("messages/completeAttachment", async ({ channel, threadId, attachmentId }, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const response = await http.post<AttachmentResponse>(
      `${base}/threads/${threadId}/attachments/${attachmentId}/complete`,
    );
    return toMessageAttachment(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toAttachmentError(error, "Failed to finish the upload"));
  }
});

/** Staff only: copies a library material into the thread as a completed attachment. */
export const attachMaterial = createAsyncThunk<
  MessageAttachment,
  { threadId: string; materialId: string },
  { rejectValue: AttachmentError }
>("messages/attachMaterial", async ({ threadId, materialId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AttachmentResponse>(
      `/messages/threads/${threadId}/attachments/from-material/${materialId}`,
    );
    return toMessageAttachment(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toAttachmentError(error, "Failed to attach the material"));
  }
});

/** A five-minute URL to an attachment's bytes, fetched when it is shown or opened. */
export const fetchAttachmentUrl = createAsyncThunk<
  AttachmentDownloadUrlResponse,
  { channel: MessagesChannel; attachmentId: string },
  { rejectValue: ApiErrorResponse }
>("messages/fetchAttachmentUrl", async ({ channel, attachmentId }, { rejectWithValue }) => {
  try {
    const { http, base } = route(channel);
    const response = await http.get<AttachmentDownloadUrlResponse>(`${base}/attachments/${attachmentId}/url`);
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to open the file"));
  }
});

const materialMapper = new MaterialResponseMapper();

/** Staff only: the organization's library for the picker (first 100, newest first as the library lists them). */
export const fetchLibraryMaterials = createAsyncThunk<Material[], void, { rejectValue: ApiErrorResponse }>(
  "messages/fetchLibraryMaterials",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<PaginatedResult<MaterialResponse>>("/materials", {
        params: { page: 1, pageSize: 100 },
      });
      return materialMapper.toMaterials(response.data.items);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, "Failed to load the library"));
    }
  },
);
